import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import FolkDivider from "@/components/FolkDivider";
import Newsletter from "@/components/Newsletter";
import { useT } from "@/i18n/useT";

export default function HomePage() {
  const t = useT();
  const h = t.home;
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true").then(({ data }) => setFeatured((data.products || []).slice(0, 3))).catch(() => {});
  }, []);

  const IMAGES = { "amber-jewelry": "https://images.pexels.com/photos/18730034/pexels-photo-18730034.jpeg", "linen-clothing": "https://images.unsplash.com/photo-1747396206869-75ea57b325ce", "baltic-decor": "https://images.unsplash.com/photo-1572853566597-b83cde546912", "gifts": "https://images.pexels.com/photos/32614625/pexels-photo-32614625.jpeg" };
  const collections = Object.entries(t.collections).map(([slug, info]) => ({ slug, ...info, image: IMAGES[slug] }));

  return (
    <div data-testid="home-page">
      <section className="relative h-[88vh] min-h-[600px] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1674142954137-3555e46e4790?w=2400&q=80)" }} />
        <div className="absolute inset-0 bg-espresso/35" />
        <div className="relative h-full br-container flex flex-col justify-end pb-24">
          <div className="max-w-2xl text-cream br-fade-in">
            <div className="text-[11px] uppercase tracking-[0.4em] text-amber mb-6">{h.eyebrow}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-cream whitespace-pre-line">{h.heroTitle}</h1>
            <p className="mt-8 text-base sm:text-lg font-light text-cream/85 max-w-lg leading-relaxed">{h.heroBody}</p>
            <div className="mt-10">
              <Link to="/shop" data-testid="hero-cta" className="inline-block bg-cream text-espresso px-8 py-4 text-xs uppercase tracking-[0.3em] font-sans hover:bg-amber hover:text-espresso transition-colors duration-500">{h.heroCta}</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="br-container py-24 md:py-32" data-testid="collections-section">
        <div className="br-fade-in mb-16 max-w-2xl">
          <div className="br-eyebrow mb-4">{h.collectionsEyebrow}</div>
          <h2 className="font-serif text-3xl md:text-5xl text-espresso leading-tight whitespace-pre-line">{h.collectionsTitle}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {collections.map((c) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} data-testid={`collection-${c.slug}`} className="br-fade-in group block">
              <div className="aspect-[3/4] overflow-hidden bg-walnut/5">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
              </div>
              <div className="mt-5">
                <h3 className="font-serif text-xl text-espresso">{c.title}</h3>
                <div className="text-xs uppercase tracking-[0.2em] text-walnut mt-1">{c.sub}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <FolkDivider />

      <section className="br-container py-20 md:py-28" data-testid="featured-section">
        <div className="br-fade-in mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-xl">
            <div className="br-eyebrow mb-4">{h.featuredEyebrow}</div>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso leading-tight">{h.featuredTitle}</h2>
          </div>
          <Link to="/shop" className="br-link-underline text-xs uppercase tracking-[0.3em] self-start md:self-end">{h.featuredCta}</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
          {featured.map((p) => <div key={p.id} className="br-fade-in"><ProductCard product={p} /></div>)}
        </div>
      </section>

      <section className="bg-walnut/5 py-24 md:py-32" data-testid="story-snippet">
        <div className="br-container grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 br-fade-in">
            <img src="https://images.unsplash.com/photo-1611056083634-5cac142017ef?w=1200&q=80" alt="Baltic coast" className="w-full h-[520px] object-cover" />
          </div>
          <div className="md:col-span-6 md:col-start-7 br-fade-in">
            <div className="br-eyebrow mb-4">{h.storyEyebrow}</div>
            <h2 className="font-serif text-3xl md:text-4xl text-espresso leading-tight">{h.storyQuote}</h2>
            <p className="mt-8 text-base font-light text-dark-walnut leading-relaxed">{h.storyBody}</p>
            <div className="mt-10"><Link to="/story" data-testid="story-cta" className="br-link-underline text-xs uppercase tracking-[0.3em]">{h.storyCta}</Link></div>
          </div>
        </div>
      </section>

      <section className="br-container py-28 md:py-40 text-center" data-testid="quote-section">
        <div className="br-fade-in max-w-3xl mx-auto">
          <div className="br-eyebrow mb-8">{h.dainaEyebrow}</div>
          <blockquote className="font-serif text-2xl md:text-4xl text-espresso italic leading-relaxed whitespace-pre-line">{h.dainaQuote}</blockquote>
          <p className="mt-6 text-sm font-light text-walnut italic">{h.dainaTranslation}</p>
        </div>
      </section>

      <section className="bg-espresso text-cream py-24" data-testid="newsletter-section">
        <div className="br-container grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber mb-4">{h.newsletterEyebrow}</div>
            <h2 className="font-serif text-3xl md:text-4xl text-cream leading-tight whitespace-pre-line">{h.newsletterTitle}</h2>
          </div>
          <div><Newsletter inverse /></div>
        </div>
      </section>
    </div>
  );
}